import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";

// Lightbox para a galeria de gala. Lê os items diretamente do DOM
// (cada card carrega seus próprios data-lightbox-* attributes), o que permite
// que cards apendados via Turbo Stream (infinite scroll) funcionem
// automaticamente sem precisar atualizar nenhum value do controller.
export default class extends Controller {
  static targets = [
    "modal",
    "mediaContainer",
    "albumTitle",
    "eventTitle",
    "counter",
    "downloadBtn",
    "shareBtn",
    "prevBtn",
    "nextBtn",
  ];
  static values = {
    albumName: String,
    eventName: String,
  };

  connect() {
    this.modal = new Modal(this.modalTarget);
    this.currentIndex = -1;
    this._onKeydown = this.#handleKeydown.bind(this);
    this._onModalHidden = this.#handleModalHidden.bind(this);
    document.addEventListener("keydown", this._onKeydown);
    this.modalTarget.addEventListener("hidden.bs.modal", this._onModalHidden);
    this.#openFromHash();
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown);
    this.modalTarget.removeEventListener("hidden.bs.modal", this._onModalHidden);
    this.modal?.dispose();
  }

  open(event) {
    const card = event.currentTarget.closest("[data-lightbox-item]");
    this.#openCard(card);
  }

  prev() {
    const cards = this.#allCards();
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
      this.#render(cards);
    }
  }

  next() {
    const cards = this.#allCards();
    if (this.currentIndex < cards.length - 1) {
      this.currentIndex += 1;
      this.#render(cards);
    }
  }

  share() {
    const cards = this.#allCards();
    const item = this.#cardData(cards[this.currentIndex]);
    if (!item) return;
    const shareUrl = window.location.href;
    if (navigator.share) {
      navigator.share({ title: this.eventNameValue, text: item.title || this.albumNameValue, url: shareUrl })
        .catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).then(() => this.#flashShareCopied());
    }
  }

  #allCards() {
    return Array.from(this.element.querySelectorAll("[data-lightbox-item]"));
  }

  #cardData(card) {
    if (!card) return null;
    return {
      id:          card.dataset.lightboxId,
      type:        card.dataset.lightboxType,
      url:         card.dataset.lightboxUrl,
      downloadUrl: card.dataset.lightboxDownload,
      filename:    card.dataset.lightboxFilename,
      title:       card.dataset.lightboxTitle || "",
    };
  }

  #openCard(card) {
    if (!card) return;
    const cards = this.#allCards();
    this.currentIndex = cards.indexOf(card);
    this.#render(cards);
    this.modal.show();
  }

  #openFromHash() {
    const id = this.#hashId();
    if (id == null) return;
    const cards = this.#allCards();
    const card = cards.find((c) => c.dataset.lightboxId === id);
    this.#openCard(card);
  }

  #hashId() {
    const m = window.location.hash.match(/^#m=(\d+)/);
    return m ? m[1] : null;
  }

  #handleModalHidden() {
    if (window.location.hash.startsWith("#m=")) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }

  #handleKeydown(e) {
    if (!this.modalTarget.classList.contains("show")) return;
    if (e.key === "ArrowLeft")  this.prev();
    if (e.key === "ArrowRight") this.next();
    if (e.key === "Escape")     this.modal.hide();
  }

  #render(cards) {
    const card = cards[this.currentIndex];
    const item = this.#cardData(card);
    if (!item) return;

    const isFirst = this.currentIndex === 0;
    const isLast  = this.currentIndex === cards.length - 1;

    if (item.type === "video") {
      this.mediaContainerTarget.innerHTML = `
        <video src="${item.url}" controls autoplay playsinline class="gala-lightbox-video" preload="metadata"></video>`;
    } else {
      this.mediaContainerTarget.innerHTML = `
        <img src="${item.url}" alt="${this.#escape(item.title || this.albumNameValue)}" class="gala-lightbox-img">`;
    }

    if (this.hasAlbumTitleTarget) this.albumTitleTarget.textContent = item.title || this.albumNameValue;
    if (this.hasEventTitleTarget) this.eventTitleTarget.textContent = this.eventNameValue;
    if (this.hasCounterTarget)    this.counterTarget.textContent    = `${this.currentIndex + 1} / ${cards.length}`;

    if (item.id) {
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}#m=${item.id}`);
    }

    if (this.hasDownloadBtnTarget) {
      this.downloadBtnTarget.href = item.downloadUrl || item.url;
      this.downloadBtnTarget.setAttribute("download", item.filename || "");
    }

    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = isFirst;
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = isLast;
  }

  #flashShareCopied() {
    if (!this.hasShareBtnTarget) return;
    const original = this.shareBtnTarget.innerHTML;
    this.shareBtnTarget.innerHTML = '<i class="bi bi-check-lg me-1"></i> Link copiado';
    setTimeout(() => { this.shareBtnTarget.innerHTML = original; }, 1800);
  }

  #escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }
}
